import DragCamera from './DragCamera.js';
import ImageDataManager from './ImageDataManager.js';
import ChangeTracker from './ChangeTracker.js';
import Selection from './Selection.js';
import DrawingToolKit from './DrawingToolKit.js';
import ImageResizer from './ImageResizer.js';
import SizeLabel from './SizeLabel.js';
import DragToolManager from './DragToolManager.js';
import ColorPalette from './ColorPalette.js';
import GradientPalette from './GradientPalette.js';
import ColorPicker from './ColorPicker.js';
import ExampleWindowContainer from './ExampleWindowContainer.js';
import PreviewWindowContainer from './PreviewWindowContainer.js';
import LayerSection from './LayerSection.js';
import FrameSection from './FrameSection.js';
import ObjectSection from './ObjectSection.js';
import Background from './Background.js';
import HelpSection from './HelpSection.js';
import MulLangTexts from './MulLangTexts.js';
import CursorHighlighter from './CursorHighlighter.js';
import EyedropperPopup from './EyedropperPopup.js';
import ColorManager from './ColorManager.js';
import Grid from './Grid.js';
import MainCanvas from './MainCanvas.js';
import SaveManager from './SaveManager.js';
import ToolSection from './ToolSection.js';
import ToolOptions from './ToolOptions.js';
import ToolOpionBackup from './ToolOptionBackup.js';
import ToolOptionToggler from './ToolOptionToggler.js';
import ScaleInput from './ScaleInput.js';
import Keyboard from './Keyboard.js';
import ThemeSection from './ThemeSection.js';
import InputStorage from './InputStorage.js';
import AutoFocus from './AutoFocus.js';
import ObjectManager from './ObjectManager.js';
import CollapsableElementGroup from './CollapsableElementGroup.js';

/*-------------------------------- 汎用・共通 --------------------------------*/
/*---------------- サービスワーカー ----------------*/
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register(
    new URL('service-worker.js', import.meta.url),
    {type: 'module'}
  );
}

/*---------------- 変数宣言 ----------------*/
const mulLangTexts = new MulLangTexts;
const requestDraw = () => mainCanvas.requestDraw();
const doChange = (...args) => changeTracker.do(...args);
const dragCamera = new DragCamera(() => {
  mainCanvas.requestDraw();
  cursorHighlighter.requestDraw();
}, document.querySelector('#mainCanvasContainer'), () => ({ x: window.innerWidth / 2, y: window.innerHeight / 2 }));
const objectManager = new ObjectManager(() => {
  const imageDataManager = new ImageDataManager(() => {
      dragCamera.setOuterLimit(mainCanvas.width, mainCanvas.height, imageDataManager.width, imageDataManager.height);
      scaleInputExport.update();
    },
    () => previewWindowContainer.update(),
    layerIdx => layerSection.updateThumbnail(layerIdx),
    frameIdx => {
      frameSection.updateThumbnail(frameIdx);
      if(frameIdx === 0) {
        objectSection.updateThumbnail(objectManager.getSelectedIdx());
      }
    }
  );
  const changeTracker = new ChangeTracker(imageDataManager, frameSection, layerSection, imageResizer, selection, drawingToolKit, () => toolSection.updateHistoryButtons(), requestDraw, () => objectManager.getChangeTrackers());
  return {imageDataManager, changeTracker};
}, () => {
  dragCamera.setOuterLimit(mainCanvas.width, mainCanvas.height, imageDataManager.width, imageDataManager.height);
  imageResizer.setRect(0, 0, imageDataManager.width, imageDataManager.height);
  frameSection.reset(imageDataManager.frameSz);
  requestDraw();
  previewWindowContainer.update();
  toolSection.updateHistoryButtons();
  scaleInputExport.update();
});
const imageDataManager = objectManager.getImageDataManagerProxy();
const changeTracker = objectManager.getChangeTrackerProxy();
const sizeLabel = new SizeLabel;
const imageResizer = new ImageResizer(dragCamera, sizeLabel, requestDraw, doChange);
const selection = new Selection(dragCamera, imageDataManager, sizeLabel, imageResizer, requestDraw, doChange);
const layerSection = new LayerSection(imageDataManager, selection, () => frameSection.getDisplayIdxSelected(), requestDraw, doChange);
const frameSection = new FrameSection(imageDataManager, selection, layerSection, requestDraw, doChange);
const colorManager = new ColorManager;
const eyedropperPopup = new EyedropperPopup;
const drawingToolKit = new DrawingToolKit(dragCamera, imageDataManager, changeTracker, selection, colorManager, eyedropperPopup, imageResizer, mode => toolSection.setMode(mode), requestDraw, () => keyboard.arrowKeys);
const colorPalette = new ColorPalette(colorManager, () => colorPicker.show(true));
const gradientPalette = new GradientPalette(colorManager, () => colorPicker.show(true));
const colorPicker = new ColorPicker(colorManager, colorPalette);
const dragToolManager = new DragToolManager(dragCamera, drawingToolKit, selection, imageResizer, cursor => mainCanvas.changeCursor(cursor));
const autoFocus = new AutoFocus;
const previewWindowContainer = new PreviewWindowContainer(objectManager, eyedropperPopup, colorManager, dragToolManager.buttons, (url, extension) => toolOptions.download(url, extension), autoFocus);
const objectSection = new ObjectSection(objectManager, previewWindowContainer, selection, requestDraw, mulLangTexts);
const exampleWindowContainer = new ExampleWindowContainer(mulLangTexts, autoFocus, eyedropperPopup, colorManager);
const background = new Background;
const helpSection = new HelpSection;
const cursorHighlighter = new CursorHighlighter(dragCamera, drawingToolKit);
const gridLarge = new Grid('Large', 32, 32, 2);
const gridSmall = new Grid('Small', 1, 1, 1);
const mainCanvas = new MainCanvas(dragCamera, imageDataManager, dragToolManager, background, cursorHighlighter, gridLarge, gridSmall);
const saveManager = new SaveManager(objectManager, colorPalette, gridLarge, gridSmall, objectSection, previewWindowContainer);
const toolOptionToggler = new ToolOptionToggler;
const toolSection = new ToolSection(changeTracker, drawingToolKit, selection, colorManager, toolOptionToggler);
const keyboard = new Keyboard(dragCamera, dragToolManager, drawingToolKit, selection, toolSection, () => helpSection.toggle());
const scaleInputExport = new ScaleInput('export', imageDataManager);
const toolOptionBackup = new ToolOpionBackup(saveManager, toolOptionToggler, mulLangTexts);
const toolOptions = new ToolOptions(
  dragCamera, objectManager, colorManager, dragToolManager, drawingToolKit, selection,
  colorPalette, gradientPalette, colorPicker, exampleWindowContainer, gridLarge, gridSmall, cursorHighlighter,
  saveManager, keyboard, toolOptionToggler, toolOptionBackup, scaleInputExport, toolSection, mulLangTexts, requestDraw
);
const themeSection = new ThemeSection(background, gridLarge, gridSmall, requestDraw);
const inputStorage = new InputStorage(helpSection);

new CollapsableElementGroup([layerSection.elSection, frameSection.elSection, objectSection.elSection], [layerSection.elContextMenu, frameSection.elContextMenu, objectSection.elContextMenu]);

/*---------------- 汎用イベント ----------------*/
//--- 右クリックメニュー禁止
document.oncontextmenu = function() {
  return false;
}

//--- スペルチェック禁止
document.querySelectorAll('[type="text"],textarea').forEach(v => {
  v.setAttribute('spellcheck', 'false');
});

//--- タブフォーカス禁止
document.addEventListener('keydown', e => {
  if(e.code === 'Tab') {
    e.preventDefault();
  }
});

//--- ピンチズーム禁止
document.addEventListener('wheel', e => {
  if(e.ctrlKey) {
    e.preventDefault();
  }
}, {passive: false});

//--- 遷移への警告
window.onbeforeunload = function() {
  return "Are you sure you want to leave this page? Changes you made may not be saved.";
};

//--- hasセレクターの代替
document.querySelectorAll('label.radio > input[type="radio"]').forEach(input => {
  input.addEventListener('input', e => {
    const label = e.target.parentElement;
    const name = e.target.name;
    const checked = document.querySelector(`label.radio.checked > input[type="radio"][name="${name}"]`);
    checked.parentElement.classList.remove('checked');
    label.classList.add('checked');
  });
});

/*-------------------------------- 初期化 --------------------------------*/
document.querySelectorAll('.popupLabel').forEach(v => {
  v.addEventListener('transitionend', e => {
    if(e.propertyName === 'opacity') {
      setTimeout(() => {
        v.classList.remove('show');
      }, 1000);
    }
  });
});

toolOptionBackup.updateDates();
toolOptionBackup.setBackup();

objectSection.add(0, 1, false);
objectSection.select(0);

mainCanvas.resize();
toolSection.updateHistoryButtons();

dragCamera.teleport(mainCanvas.width / 2, mainCanvas.height / 2, imageDataManager.width / 2, imageDataManager.height / 2, 4);

colorPalette.addColor([0,0,0,0]);
colorPalette.addColor([0,0,0,1]);
colorPalette.addColor([66,132,156,1]);
colorPalette.addColor([189,41,0,1]);
colorPalette.addColor([206,156,90,1]);
colorPalette.addColor([197,197,173,1]);
colorPalette.addColor([255,58,58,1]);
colorPalette.addColor([255,206,41,1]);
colorPalette.addColor([0,206,0,1]);
colorPalette.addColor([255,214,197,1]);
colorPalette.addColor([156,230,230,1]);
colorPalette.addColor([255,255,255,1]);
gradientPalette.addColor();
gradientPalette.addColor();
gradientPalette.addColor();
gradientPalette.setSrc(0, [0,0,0,1]);
gradientPalette.setSrc(1, [255,255,255,1]);
colorManager.set([0,0,0,1]);

inputStorage.init();
scaleInputExport.update();
mulLangTexts.init().then(({helps}) => {
  if(helps) helpSection.localize(helps);
});

for(const v of document.getElementById('windowContainer').children) {
  autoFocus.add(v);
}

document.addEventListener('mousedown', e => {
  if(!e.target.classList.contains('noClickLimit')) {
    e.target.classList.add('currentMousedownTarget');
  }
});
window.addEventListener('mouseup', e => {
  if(drawingToolKit.isFree()) {
    document.querySelectorAll('.currentMousedownTarget').forEach(v => {
      v.classList.remove('currentMousedownTarget');
    });
  }
});
